import { CloseOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import queryKeys from '@/configs/react-query-keys';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useModalStore from '@/stores/pure/ModalStore';
import { useHomeOnboardingGuide } from '@/stores/WorkspaceCacheStore';
import { getSourceIcon,SOURCE_TYPE } from '@/types/feedbackConst';
import { track } from '@/utils/track/track';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

const OnboardingGuide = () => {
  const sourceInfo = useFeedbackSourceStore((state) => state.sourceInfo);
  const setModalStatus = useModalStore((state) => state.setModalStatus);
  const { push } = useWorkspaceRouter();
  const appInit = useAppInit();
  const wa = getCurrWorkspaceAlias();

  const { homeOnboardingGuide, setHomeOnboardingGuide } =
    useHomeOnboardingGuide();

   
  useEffect(() => {
    if (sourceInfo && sourceInfo.length > 0) {
      setHomeOnboardingGuide({
        import_feedback: false,
      });
    }
  }, [sourceInfo]);

  const { data: taxonomyData } = useQuery({
    queryFn: () => service.taxonomy.fetchTaxonomyList(wa),
    queryKey: queryKeys.taxonomy.TAXONOMY_LIST,
    enabled: appInit && !!wa,
  });

   
  useEffect(() => {
    if (taxonomyData && taxonomyData.length > 0) {
      setHomeOnboardingGuide({
        create_taxonomy: false,
      });
    }
  }, [taxonomyData]);

  const onClickDismiss = (key: keyof typeof homeOnboardingGuide) => {
    track(AMP_KEYS.HOME_GUIDE_DISMISS_CLICK);
    setHomeOnboardingGuide({
      ...homeOnboardingGuide,
      [key]: false,
    });
  };

  const openFeedbackUploadModal = () => {
    track(AMP_KEYS.HOME_GUIDE_IMPORT_CSV_CLICK);
    push('/feedbacks/upload');
    setModalStatus('feedbackUploadModal', true);
  };

  const openIntegrationSettingPage = () => {
    track(AMP_KEYS.HOME_GUIDE_INTEGRATE_CLICK);
    push('/settings/integration');
  };

  const openTopicPage = () => {
    track(AMP_KEYS.HOME_GUIDE_CREATE_TOPIC_CLICK);
    push('/taxonomy');
  };

  if (
    !homeOnboardingGuide.create_taxonomy &&
    !homeOnboardingGuide.import_feedback
  )
    return null;

  return (
    <div className="flex flex-col w-full">
      <Flex d="column" g={20}>
        <Transition
          status={homeOnboardingGuide.import_feedback}
          style={{ flex: 1 }}
        >
          <HomeActionItem
            buttons={
              <>
                <Button
                  variant="outlined"
                  sizeType="m"
                  onClick={openFeedbackUploadModal}
                >
                  Import CSV
                </Button>
                <Button
                  variant="primary"
                  sizeType="m"
                  onClick={openIntegrationSettingPage}
                >
                  Integrate
                </Button>
              </>
            }
          >
            <NumberWrapper number={1} />
            <VFlex f={1}>
              <Flex j="space-between">
                <Typo type="label1" weight="sb" className="text-gray-900">
                  Import feedback
                </Typo>
                <IconButton
                  icon={<CloseOutlined />}
                  style={{ border: 'none' }}
                  onClick={() => onClickDismiss('import_feedback')}
                />
              </Flex>
              <Space h={2} />
              <Typo type="body2" className="text-gray-600">
                Push feedback into Syncly
              </Typo>
              <Space h={14} />
              <Flex j="space-between">
                <Flex a="center" g={12}>
                  {getSourceIcon(SOURCE_TYPE.SLACK)}
                  {getSourceIcon(SOURCE_TYPE.ZENDESK)}
                  {getSourceIcon(SOURCE_TYPE.INTERCOM)}
                  {getSourceIcon(SOURCE_TYPE.DISCORD)}
                </Flex>
              </Flex>
            </VFlex>
          </HomeActionItem>
        </Transition>
        <Transition
          status={homeOnboardingGuide.create_taxonomy}
          style={{ flex: 1 }}
        >
          <HomeActionItem
            buttons={
              <Button variant="primary" sizeType="m" onClick={openTopicPage}>
                Create taxonomy
              </Button>
            }
          >
            <NumberWrapper number={2} />
            <VFlex f={1}>
              <Flex j="space-between">
                <Typo type="label1" weight="sb" className="text-gray-900">
                  Create taxonomy
                </Typo>
                <IconButton
                  icon={<CloseOutlined />}
                  style={{ border: 'none' }}
                  onClick={() => onClickDismiss('create_taxonomy')}
                />
              </Flex>
              <Space h={2} />
              <Typo
                type="body2"
                className="text-gray-600"
                style={{ maxWidth: 'calc(100% - 120px)' }}
              >
                Build your own taxonomy with the help from AI.
              </Typo>
            </VFlex>
          </HomeActionItem>
        </Transition>
      </Flex>
    </div>
  );
};

const NumberWrapper = ({ number }: { number: number }) => {
  return (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-900 text-sm font-medium border border-gray-400">
      {number}
    </div>
  );
};

const HomeActionItem = ({
  children,
  buttons,
}: {
  children: React.ReactNode;
  buttons: React.ReactNode;
}) => {
  return (
    <div className="flex relative gap-3 h-full bg-gray-150 border border-gray-300 rounded-lg p-4 min-h-28">
      {children}
      <div className="absolute right-4 bottom-4 flex gap-2">{buttons}</div>
    </div>
  );
};

export default OnboardingGuide;
